import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
});

export const useTheme = () => {
  return useContext(ThemeContext);
};

// Resolve the active theme on the client from the class already applied to
// <html> by the pre-hydration script (see gatsby-ssr/gatsby-browser), falling
// back to the persisted preference. On the server we always start with 'light'.
const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  if (document.documentElement.classList.contains('dark')) {
    return 'dark';
  }

  try {
    return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
  } catch (e) {
    return 'light';
  }
};

export const ThemeProvider = ({ children }) => {
  // The provider is mounted from the very first render so the page subtree is
  // never remounted on hydration. The saved theme is applied via the
  // pre-hydration script and kept in sync through the effect below.
  const [theme, setTheme] = useState(getInitialTheme);

  // Keep the document class and persisted preference in sync with state.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    try {
      localStorage.setItem('theme', theme);
    } catch (e) {
      // Ignore storage errors (e.g. private mode); theming still works.
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
